import { PaperClipIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { DocumentCheckIcon, TrashIcon } from '@/components/icons'

import { ACCEPTED_EXTENSIONS } from '../constants'

import { ROW_STATUS, summarizeRows, summaryLabel } from './parse'

const PILLS = {
  [ROW_STATUS.NEW]: { className: 'bg-green-100 text-green-900', label: 'New' },
  [ROW_STATUS.DUPLICATE]: {
    className: 'bg-charcoal-100 text-charcoal-700',
    label: 'Already logged',
  },
  [ROW_STATUS.INVALID]: { className: 'bg-red-100 text-red-900', label: 'Unusable' },
}

const StatusPill = ({ status }) => {
  const pill = PILLS[status] || PILLS[ROW_STATUS.INVALID]

  return (
    <span
      className={mergeClassNames(
        'inline-block whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium uppercase',
        pill.className,
      )}
    >
      {pill.label}
    </span>
  )
}

StatusPill.propTypes = { status: PropTypes.string.isRequired }

const previewCountLabel = ({ loadingDuplicates, rows }) => {
  if (loadingDuplicates) return 'Checking for duplicates…'
  if (rows.length === 0) return 'Nothing to preview yet'
  return `${rows.length} RO${rows.length === 1 ? '' : 's'} in preview`
}

/**
 * The right half of the Log ROs screen: every number the user has entered, what will
 * happen to it, and the one button that writes anything.
 *
 * Nothing is posted until "Log N ROs" is pressed, and N counts only the new rows —
 * already-logged numbers are reported and skipped.
 */
const PreviewPanel = ({
  committing,
  loadingDuplicates,
  onAddFiles,
  onClear,
  onCommit,
  onRemoveFile,
  onRemoveRow,
  rows,
}) => {
  const fileInputRefs = useRef({})
  const { newCount } = summarizeRows(rows)

  return (
    <div className="flex flex-col rounded-2xl bg-white shadow-sm">
      <div className="border-charcoal-100 flex items-center justify-between border-b px-6 py-4">
        <h2 className="text-charcoal-900 text-lg font-semibold">Preview</h2>
        <span className="text-charcoal-500 text-sm">
          {previewCountLabel({ loadingDuplicates, rows })}
        </span>
      </div>

      <div className="max-h-[520px] min-h-[220px] flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="text-charcoal-400 px-6 py-16 text-center text-sm">
            Repair orders you add show here before any of them are logged.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-background text-charcoal-700 sticky top-0 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="px-6 py-2 text-left font-bold">RO Number</th>
                <th className="px-3 py-2 text-left font-bold">Source</th>
                <th className="px-3 py-2 text-left font-bold">Status</th>
                <th className="px-3 py-2 text-right font-bold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  className={mergeClassNames(
                    'border-charcoal-100 border-b align-top',
                    row.status !== ROW_STATUS.NEW && 'bg-background/60',
                  )}
                  key={row.id}
                >
                  <td className="px-6 py-3">
                    <span
                      className={mergeClassNames(
                        'font-mono',
                        row.status === ROW_STATUS.NEW
                          ? 'text-charcoal-900'
                          : 'text-charcoal-400 line-through',
                      )}
                    >
                      {row.roNumber}
                    </span>
                    {row.note && <p className="text-charcoal-400 mt-1 text-xs">{row.note}</p>}
                    {row.error && <p className="text-red mt-1 text-xs">{row.error}</p>}
                    {row.files.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {row.files.map((entry) => (
                          <div
                            className="flex items-center justify-between text-blue-800"
                            key={entry.id}
                          >
                            <div className="flex items-center overflow-x-hidden">
                              <DocumentCheckIcon className="mr-2 h-4 w-4 shrink-0" />
                              <span className="truncate text-xs underline">{entry.raw.name}</span>
                            </div>
                            <button
                              aria-label={`Remove ${entry.raw.name}`}
                              className="shrink-0 rounded-lg p-1 hover:bg-gray-100"
                              onClick={() => onRemoveFile(row.id, entry.id)}
                              type="button"
                            >
                              <TrashIcon className="text-charcoal-800 h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="text-charcoal-500 p-3 text-xs">{row.source}</td>
                  <td className="p-3">
                    <StatusPill status={row.status} />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {row.status === ROW_STATUS.NEW && (
                        <>
                          <input
                            accept={ACCEPTED_EXTENSIONS.join(',')}
                            className="hidden"
                            multiple
                            onChange={(event) => {
                              if (event.target.files.length > 0) {
                                onAddFiles(row.id, Array.from(event.target.files))
                              }
                              // Reset through the ref so picking the same file twice
                              // still fires a change.
                              const input = fileInputRefs.current[row.id]
                              if (input) input.value = ''
                            }}
                            ref={(element) => {
                              fileInputRefs.current[row.id] = element
                            }}
                            type="file"
                          />
                          <button
                            aria-label={`Attach files to ${row.roNumber}`}
                            className="text-charcoal-400 rounded-lg p-1.5 hover:bg-gray-100 hover:text-blue-800"
                            onClick={() => fileInputRefs.current[row.id]?.click()}
                            type="button"
                          >
                            <PaperClipIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        aria-label={`Remove ${row.roNumber} from the preview`}
                        className="text-charcoal-400 hover:text-red rounded-lg p-1.5 hover:bg-gray-100"
                        onClick={() => onRemoveRow(row.id)}
                        type="button"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-charcoal-100 flex items-center justify-between gap-4 border-t px-6 py-4">
        <span className="text-charcoal-700 text-sm font-medium">{summaryLabel(rows)}</span>
        <div className="flex items-center gap-2">
          <button
            className="text-charcoal-700 hover:bg-charcoal-100 rounded px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:cursor-not-allowed disabled:opacity-50"
            disabled={rows.length === 0 || committing}
            onClick={onClear}
            type="button"
          >
            Clear
          </button>
          <button
            className="rounded border-[1.5px] border-blue-800 bg-blue-800 px-4 py-2 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-blue-900 hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={newCount === 0 || committing || loadingDuplicates}
            onClick={onCommit}
            type="button"
          >
            {committing ? 'Logging…' : `Log ${newCount} RO${newCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  )
}

PreviewPanel.propTypes = {
  committing: PropTypes.bool,
  loadingDuplicates: PropTypes.bool,
  onAddFiles: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onCommit: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  onRemoveRow: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default PreviewPanel
